import React, { useState, useEffect } from "react";

import {
	Section,
	OneColumnStack,
	RHFTextField,
	RHFSelect,
	RHFDateTextField,
	RHFCheckbox,
	ButtonStack,
	Button,
} from "@arp/react-ndbx";

//! TEST
// import { RHFSelect } from "../../coding/rhfSelect";
//! TEST END

import { useOutletContext } from "react-router-dom";

//** Page-Level Information */

//** Config */
import {
	questionTypes,
	questionLabels,
	notificationCardItems,
} from "../../../config/questions/questionItems.js";

import { yourDetailsPersonalFieldNames } from "../../../config/pageInfo/pageFieldName";
import { yourDetailsPersonalTooltips } from "../../../config/tooltips/pageTooltips";
import { yourDetailsPersonalNotification } from "../../../config/notifications/pageNotifications";

//** Application components */
import ToggleButtonNotification from "../../organisms/ToggleButtonNotification.jsx";
import AdditionalOwnerAccordion from "../sharedComponents/AdditionalOwnerAccordion.jsx";
import { isAdditionalPolicyPresent } from "../../../utilities/preQualificationUtils.js";

//----------------------------------------------------------------

const PersonalInformation = ({ ...props }) => {
	const { person, dataSet, titleItems, errors } = { ...props };

	const {
		methods: [methods],
		showSecond: [showSecond],
		isDirty: [isDirty],
		isValid: [isValid],
	} = useOutletContext();

	const { control, reset, resetField, setValue, trigger, getValues } = {
		...methods,
	};

	//! TODO: REMOVE THIS IN ALL PLACES ON THE PAGE
	//! ONCE THE UPDATE TO THE API IS COMPLETE
	//! RIO-14889
	const isBackEndApiWorkComplete = true;

	// ****************************************************************
	//** Permanent resident of Australia  */
	const [showResidentQuestionError, setShowResidentQuestionError] =
		useState(false);

	useEffect(() => {
		const outputResidentQuestion =
			yourDetailsPersonalFieldNames(person).RESIDENT_QUESTION in errors[0];
		setShowResidentQuestionError(outputResidentQuestion);
	}, [errors]);

	useEffect(() => {
		if (showSecond === false) {
			resetField(yourDetailsPersonalFieldNames("personTwo").RESIDENT_QUESTION, {
				defaultValue: null,
			});
			resetField(yourDetailsPersonalFieldNames("personTwo").TITLE, {
				defaultValue: "",
			});
			resetField(yourDetailsPersonalFieldNames("personTwo").FIRST_NAME, {
				defaultValue: "",
			});
			resetField(yourDetailsPersonalFieldNames("personTwo").SURNAME, {
				defaultValue: "",
			});
			resetField(yourDetailsPersonalFieldNames("personTwo").GENDER, {
				defaultValue: "",
			});
			resetField(yourDetailsPersonalFieldNames("personTwo").DOB, {
				defaultValue: null,
			});
		}
	}, [showSecond]);

	//----------------------------------------------------------------

	useEffect(() => {
		if (dataSet?.customerRetrieveQuoteLoaded === false) return;

		setValue(
			yourDetailsPersonalFieldNames(person).RESIDENT_QUESTION,
			dataSet?.[person].residentQuestion,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsPersonalFieldNames(person).TITLE,
			dataSet?.[person].title,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsPersonalFieldNames(person).FIRST_NAME,
			dataSet?.[person].firstName,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsPersonalFieldNames(person).SURNAME,
			dataSet?.[person].surname,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsPersonalFieldNames(person).GENDER,
			dataSet?.[person].gender,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsPersonalFieldNames(person).DOB,
			dataSet?.[person]?.dob,
			{ shouldDirty: true }
		);

		if (isAdditionalPolicyPresent(dataSet)) {
			setValue(
				yourDetailsPersonalFieldNames(person).ADDITIONAL_HOLDER_TITLE,
				dataSet.additionalPolicyHolderTitle,
				{ shouldDirty: true }
			);
			setValue(
				yourDetailsPersonalFieldNames(person).ADDITIONAL_HOLDER_FIRSTNAME,
				dataSet.additionalPolicyHolderFirstName,
				{ shouldDirty: true }
			);
			setValue(
				yourDetailsPersonalFieldNames(person).ADDITIONAL_HOLDER_SURNAME,
				dataSet.additionalPolicyHolderSurname,
				{ shouldDirty: true }
			);
			setValue(
				yourDetailsPersonalFieldNames(person).ADDITIONAL_HOLDER_DOB,
				dataSet.additionalPolicyHolderDOB,
				{ shouldDirty: true }
			);
		}

		trigger(); //* ensure that there are no errors on the page
	}, [dataSet?.customerRetrieveQuoteLoaded]);

	//----------------------------------------------------------------

	return (
		<>
			<Section md={12} mt={5} id="your-details-personal">
				<OneColumnStack
					name="your-details-personal-toggle-questions"
					sx={{ alignItems: { xs: "center", md: "flex-start" } }}
				>
					<ToggleButtonNotification
						dataSetItem={dataSet[person].residentQuestion}
						fieldName={yourDetailsPersonalFieldNames(person).RESIDENT_QUESTION}
						label={questionLabels.RESIDENT_QUESTION}
						items={questionTypes.YES_NO}
						notificationCard={notificationCardItems(
							yourDetailsPersonalNotification.PERMANENT_RESIDENT_NOTIFICATION
								.cardType,
							yourDetailsPersonalNotification.PERMANENT_RESIDENT_NOTIFICATION
								.title,
							yourDetailsPersonalNotification.PERMANENT_RESIDENT_NOTIFICATION
								.message,
							yourDetailsPersonalNotification.PERMANENT_RESIDENT_NOTIFICATION
								.additionalText
						)}
						showNotification={showResidentQuestionError}
						showTooltip={true}
						tooltipText={yourDetailsPersonalTooltips.PERMANENT_RESIDENT_TOOLTIP}
						errorVariant={showResidentQuestionError}
					/>
				</OneColumnStack>
			</Section>
			<OneColumnStack id="your-details-personal-question-layout-section">
				<Section
					id="title-select-section"
					md={12}
					mt={3}
					align="left"
					sx={{ outline: "0px blue solid" }}
				>
					<RHFSelect
						defaultValue={dataSet[person]?.title}
						name={yourDetailsPersonalFieldNames(person).TITLE}
						label="Title"
						variant="standard"
						items={titleItems ? titleItems : []}
					/>
				</Section>
				<Section md={12} mt={{ sm: 2, md: 4 }}>
					<RHFTextField
						name={yourDetailsPersonalFieldNames(person).FIRST_NAME}
						defaultValue={dataSet[person].firstName}
						className="subvariant-text-input"
						variant="standard"
						label="First name"
						sx={{ textTransform: "uppercase" }}
					/>
				</Section>
				<Section md={12} mt={{ sm: 2, md: 4 }}>
					<RHFTextField
						name={yourDetailsPersonalFieldNames(person).SURNAME}
						defaultValue={dataSet[person].surname}
						className="subvariant-text-input"
						variant="standard"
						label="Surname"
					/>
				</Section>
				<Section
					md={12}
					mt={{ sm: 2, md: 6 }}
					align="left"
					sx={{ outline: "0px blue solid" }}
				>
					<RHFSelect
						name={yourDetailsPersonalFieldNames(person).GENDER}
						defaultValue={dataSet[person].gender}
						label="Gender"
						variant="standard"
						items={[
							{ name: "Female", value: "female" },
							{ name: "Male", value: "male" },
						]}
					/>
				</Section>
				<Section
					md={12}
					mt={{ sm: 2, md: 6 }}
					align="left"
					sx={{ outline: "0px blue solid" }}
				>
					<RHFDateTextField
						name={yourDetailsPersonalFieldNames(person).DOB}
						defaultValue={dataSet[person].dob}
						variant="standard"
						label="Date of birth"
						showErrorCard={true}
					/>
				</Section>
			</OneColumnStack>

			{isBackEndApiWorkComplete == true ? (
				<Section md={10}>
					<AdditionalOwnerAccordion
						titleItems={titleItems}
						reset={reset}
						resetField={resetField}
						setValue={setValue}
						dataSet={dataSet}
						person={person}
						getValues={getValues}
						control={control}
					/>
				</Section>
			) : null}
		</>
	);
};

export default PersonalInformation;
